import React, { useState } from "react";
import { Loader2, PieChart, RotateCcw, ExternalLink, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, unwrap } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const money = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

const PAYOUT_BADGE = {
  pending: { cls: "bg-stone-100 text-stone-600 border-stone-200", icon: Clock, label: "Pending" },
  held: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle, label: "Held" },
  paid: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Paid" },
  failed: { cls: "bg-rose-50 text-rose-700 border-rose-200", icon: AlertTriangle, label: "Failed" },
};

/** Commission breakdown + payout state for one booking (admin only). */
export default function CommissionBreakdown({ booking, locale, onChanged }) {
  const [retrying, setRetrying] = useState(false);
  const split = booking.commission_split;
  const status = booking.payout_status || "pending";
  const meta = PAYOUT_BADGE[status] || PAYOUT_BADGE.pending;
  const Icon = meta.icon;
  const reason = booking.payout_held_reason || booking.payout_failure_reason || "";
  // A transfer that already left the platform but bounced at the cleaner's bank
  // must NOT be retried — Stripe retries the bank payout itself once the account
  // details are fixed, and a second Transfer would pay the cleaner twice.
  const bankFailed = status === "failed" && !!booking.payout_transfer_id;
  const canRetry = booking.status === "completed" && !booking.payout_transfer_id && ["held", "failed"].includes(status);

  const retry = async () => {
    setRetrying(true);
    try {
      const r = await api.post(`/admin/bookings/${booking.id}/retry-payout`);
      const d = unwrap(r);
      if (d?.ok === false) {
        toast.error(`Payout still ${d.payout_status}: ${d.reason_label || d.reason}`);
      } else if (d?.already_paid) {
        toast.info("This payout was already sent");
      } else {
        toast.success(`Payout sent — ${money(d.amount_cents)}`);
      }
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Retry failed");
    } finally { setRetrying(false); }
  };

  return (
    <Card className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid="commission-breakdown-section">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <h3 className="font-display font-bold flex items-center gap-2">
          <PieChart className="w-4 h-4 text-[#0EA5A4]" /> Commission breakdown
        </h3>
        <Badge className={`border font-medium ${meta.cls}`} data-testid="payout-status-badge">
          <Icon className="w-3 h-3 mr-1" /> Payout {meta.label.toLowerCase()}
        </Badge>
      </div>

      {!split ? (
        <p className="text-sm text-[#4B5563]" data-testid="commission-not-calculated">
          The 45/55 split has not been calculated for this booking yet. It's frozen the moment the
          payment is confirmed — or computed automatically when the booking is marked completed.
        </p>
      ) : (
        <dl className="text-sm space-y-2">
          <div className="flex justify-between">
            <dt className="text-[#4B5563]">Total charged</dt>
            <dd className="font-mono font-semibold" data-testid="commission-total">{money(split.total_cents)}</dd>
          </div>
          <div className="flex justify-between text-emerald-700">
            <dt>Cleaner share ({Math.round(split.commission_percent_cleaner)}%)</dt>
            <dd className="font-mono font-semibold" data-testid="commission-cleaner-share">{money(split.cleaner_cents)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[#4B5563]">Platform share ({Math.round(split.commission_percent_platform)}%)</dt>
            <dd className="font-mono font-semibold" data-testid="commission-platform-share">{money(split.platform_cents)}</dd>
          </div>
          {split.backfilled && (
            <div className="text-xs text-[#9CA3AF] pt-1" data-testid="commission-backfilled-note">
              Calculated at completion time (this booking was paid before the split was tracked).
            </div>
          )}
        </dl>
      )}

      <div className="mt-4 pt-4 border-t border-[#E5E7EB] text-sm space-y-2">
        {status === "paid" && (
          <div className="flex justify-between gap-4 items-center">
            <span className="text-[#4B5563]">Paid on</span>
            <span className="text-right" data-testid="payout-paid-on">
              <span className="font-semibold">{booking.payout_completed_at ? formatDateTime(booking.payout_completed_at, locale) : "—"}</span>
              {booking.payout_transfer_id && (
                <>
                  {" "}
                  <a href={`https://dashboard.stripe.com/connect/transfers/${booking.payout_transfer_id}`}
                    target="_blank" rel="noreferrer"
                    className="font-mono text-xs text-[#0F766E] hover:underline inline-flex items-center gap-1"
                    data-testid="payout-transfer-link">
                    ({booking.payout_transfer_id}) <ExternalLink className="w-3 h-3" />
                  </a>
                </>
              )}
            </span>
          </div>
        )}
        {status === "paid" && booking.payout_arrived_at && (
          <div className="flex justify-between">
            <span className="text-[#4B5563]">Arrived in bank</span>
            <span className="font-semibold">{formatDateTime(booking.payout_arrived_at, locale)}</span>
          </div>
        )}
        {reason && status !== "paid" && (
          bankFailed ? (
            <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-900 px-4 py-3 text-sm space-y-1.5" data-testid="payout-bank-failed-note">
              <div><b>The bank rejected this payout.</b> <span className="font-mono text-xs">{reason}</span></div>
              <div className="text-xs leading-relaxed">
                The transfer <span className="font-mono">{booking.payout_transfer_id}</span> already left the platform,
                so the money is sitting in the cleaner's Stripe balance. Fix their bank details in Stripe and Stripe
                retries the payout on its own — creating a second transfer here would pay them twice, so
                "Retry payout" is intentionally disabled.
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 text-sm" data-testid="payout-hold-reason">
              <b>Payout on hold.</b> <span className="font-mono text-xs">{reason}</span>
            </div>
          )
        )}
        {status === "pending" && !reason && (
          <p className="text-xs text-[#9CA3AF]" data-testid="payout-pending-note">
            The cleaner's share is released automatically the first time this booking is marked completed.
          </p>
        )}
        {canRetry && (
          <Button onClick={retry} disabled={retrying} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white mt-2" data-testid="retry-payout-btn">
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-2" /> Retry payout</>}
          </Button>
        )}
      </div>
    </Card>
  );
}
